<!--

	Default website check box control.

-->

<template>
	<label class="mia-TickBox" :class="{disabled}">
		<input
			ref="checkbox"
			:checked="checked"
			type="checkbox"
			:disabled="disabled"
			:indeterminate="indeterminate"
			@input="input"
		>
		<span class="checkmark" :class="{ 'mia-indeterminate': indeterminate }" />
		<slot />
	</label>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, toNative } from "vue-facing-decorator";

@Component({
	emits: ["update:modelValue"],
})
export class CheckBox extends Vue {
	@Prop({ required: true, type: Boolean }) private modelValue!: boolean;
	@Prop({ required: false, default: false, type: Boolean }) indeterminate!: boolean;

	/**
	 * Indicates whether the checkbox is disabled (non-interactive).
	 * It is mainly used as a lock during requests to backend.
	 * _Consider using {@link ReadOnlyCheckBox} if you need a checkbox that also looks disabled._
	 */
	@Prop({ required: false, type: Boolean }) disabled!: boolean;

	checked = false;

	@Watch("modelValue", { immediate: true })
	private onSet(value: boolean) {
		this.checked = value;
	}

	input(e: any) {
		this.checked = e.target.checked;
		this.$emit("update:modelValue", this.checked);
	}
}

export default toNative(CheckBox);
</script>

<style lang="scss">
// We are using vue-js-toggle-button style from https://www.npmjs.com/package/vue-js-toggle-button
// From now this is the new check boxes style in the entire site
// We called it mia-TickBox since mia-CheckBox is already taken

$mia-tickbox-indeterminate-dash-width: 10px;

/* Customize the label (the container) */
.mia-TickBox {
	display: flex;
	align-items: center;
	position: relative;
	font-size: theme.$font-size-base;
	font-weight: normal;
	user-select: none;
	margin-bottom: 0;
}

.mia-TickBox:not(.disabled) {
	cursor: pointer;
}

.mia-TickBox.disabled input ~ .checkmark {
	background-color: theme.$mia-color-lighterGray;
	border: solid 1px theme.$mia-color-gray;
}

/* Hide the browser's default checkbox */
.mia-TickBox input {
	position: absolute;
	opacity: 0;
	height: 0;
	width: 0;
}

.mia-TickBox {
	.checkmark {
		margin-left: theme.$mia-icon-margin;
		margin-right: theme.$mia-icon-margin * 2;
		position: unset;
	}
	.checkmark:after {
		position: unset;
		margin-top: 3px;
		margin-left: 7px;
	}
}

/* Create a custom checkbox */
.checkmark {
	position: absolute;
	top: 0;
	left: 0;
	height: 20px;
	width: 20px;
	min-width: 20px;
	background-color: theme.$mia-color-lighterGray;
	border: solid 1px theme.$mia-color-darkGray;
	border-radius: theme.$mia-border-radius-small;
}

/* On mouse-over, add a grey background color */
.mia-TickBox:not(.disabled):hover input ~ .checkmark {
	background-color: theme.$mia-color-gray;
}

/* When the checkbox is checked, add a background */
.mia-TickBox input:checked ~ .checkmark {
	background-color: theme.$mia-color-themeMain;
	border: solid 1px theme.$mia-color-lighterGray;
}

/* When selected checkbox is checked, darken the background */
.mia-TickBox:not(.disabled):hover input:checked ~ .checkmark {
	background-color: theme.$mia-color-themeMain-dark;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
	content: "";
	position: absolute;
	display: none;
}

/* Show the checkmark when checked */
.mia-TickBox input:checked ~ .checkmark:after {
	display: block;
}

/* Style the checkmark/indicator */
.mia-TickBox .checkmark:after {
	left: 7px;
	top: theme.$mia-padding-panel-small;
	width: 5px;
	height: 10px;
	border: solid theme.$mia-color-white;
	border-width: 0 theme.$mia-border-width-panel theme.$mia-border-width-panel 0;
	transform: rotate(45deg);
}

.mia-TickBox .checkmark.mia-indeterminate {
	background-color: theme.$mia-color-themeMain;
	border: solid theme.$mia-border-width-panel-small theme.$mia-color-lighterGray;

	&:not(.disabled):hover {
		background-color: theme.$mia-color-themeMain-dark;
	}
}

.mia-TickBox .checkmark.mia-indeterminate:after {
	display: block;
	position: absolute;
	margin: 0;
	left: 50%;
	top: 50%;
	width: $mia-tickbox-indeterminate-dash-width;
	height: 0;
	border: solid theme.$mia-color-white;
	border-width: 0 0 theme.$mia-border-width-panel 0;
	transform: translate(-50%, -50%);
}
</style>
